import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import PageContainer from "./PageContainer";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import EventIcon from "@mui/icons-material/Event";
import "../assets/events.css";
import EventFilterPop from "../components/EventFilterPop";
import axios from "axios";
import Loader from "../components/Utils/Loader";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // Store all events
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [eventDetails, setEventDetails] = useState({});
  const [loadingEventDetails, setLoadingEventDetails] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    location: [],
    organization: [],
    date: [],
  });
  const [openFilter, setOpenFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventErrors, setEventErrors] = useState({});

  // Fetch top-level events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/api/events", {
          withCredentials: true,
        });
        setAllEvents(response.data); // Store all events
        setEvents(response.data); // Initially display all events
        setError(null);
      } catch (err) {
        console.error("Failed to fetch events:", err);
        setError("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filter events when selectedFilters change
  useEffect(() => {
    if (allEvents.length === 0) return;

    const filteredEvents = allEvents.filter((event) => {
      // Check location filter
      if (selectedFilters.location.length > 0) {
        // Skip this event if it has no location or the location isn't in the filter
        if (
          !event.location ||
          !selectedFilters.location.includes(event.location)
        ) {
          return false;
        }
      }

      // Check organization filter (which is the event name)
      if (selectedFilters.organization.length > 0) {
        // Skip this event if it has no name or the name isn't in the filter
        if (!event.name || !selectedFilters.organization.includes(event.name)) {
          return false;
        }
      }

      // Check date filter (this is more complex and would need parsing)
      if (selectedFilters.date.length > 0) {
        // Skip this event if it has no date
        if (!event.date) {
          return false;
        }

        // Extract month and year from event date (format: "Mon DD-DD, YYYY")
        const dateMatch = event.date.match(/^(\w+).*?(\d{4})$/);
        if (dateMatch) {
          const [_, month, year] = dateMatch;
          const monthYear = `${month} ${year}`;
          if (!selectedFilters.date.includes(monthYear)) {
            return false;
          }
        } else {
          return false;
        }
      }

      return true;
    });

    setEvents(filteredEvents);
  }, [selectedFilters, allEvents]);

  // Fetch event details when an event is expanded
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!expandedEvent || eventDetails[expandedEvent]) {
        return; // Already fetched or no event selected
      }

      try {
        setLoadingEventDetails(true);
        const response = await axios.get(`/api/events/${expandedEvent}`, {
          withCredentials: true,
        });

        setEventDetails((prev) => ({
          ...prev,
          [expandedEvent]: response.data,
        }));

        // Clear any previous error for this event
        setEventErrors((prev) => ({
          ...prev,
          [expandedEvent]: null,
        }));
      } catch (err) {
        console.error(
          `Failed to fetch event details for ${expandedEvent}:`,
          err
        );
        setEventErrors((prev) => ({
          ...prev,
          [expandedEvent]: "Failed to load event details. Please try again.",
        }));
      } finally {
        setLoadingEventDetails(false);
      }
    };

    fetchEventDetails();
  }, [expandedEvent, eventDetails]);

  const handleEventClick = (eventId, e) => {
    e.preventDefault();
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  const formatPlayerName = (fullName) => {
    const names = fullName.split(" ");
    return `${names[0][0]}. ${names[names.length - 1]}`;
  };

  const handleFilterClick = (filterType) => {
    setOpenFilter(filterType);
  };

  const handleFilterClose = () => {
    setOpenFilter(null);
  };

  const handleFilterSave = (filterType, selectedValues) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: selectedValues,
    }));
  };

  const getFilterDisplayText = (filterType) => {
    const selected = selectedFilters[filterType];
    if (!selected || selected.length === 0) return "All";
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  };

  const handleRetryEventDetails = (eventId) => {
    // Remove the cached details to force a refresh
    const newEventDetails = { ...eventDetails };
    delete newEventDetails[eventId];
    setEventDetails(newEventDetails);

    // Clear the error
    setEventErrors((prev) => ({
      ...prev,
      [eventId]: null,
    }));

    // Force a refetch
    setExpandedEvent(null);
    setTimeout(() => setExpandedEvent(eventId), 100);
  };

  return (
    <PageContainer>
      <div className="event-filter-menu">
        <div className="event-filter-menu-filters">
          {["location", "organization", "date"].map((filterType) => (
            <button
              key={filterType}
              className={`player-gallery-menu-btn ${
                selectedFilters[filterType].length > 0 ? "has-selection" : ""
              }`}
              onClick={() => handleFilterClick(filterType)}
            >
              <span className="player-gallery-menu-btn-label">
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </span>
              <span className="player-gallery-menu-btn-value">
                {getFilterDisplayText(filterType)}
              </span>
            </button>
          ))}
        </div>
      </div>

      <EventFilterPop
        open={!!openFilter}
        filterType={openFilter}
        onClose={handleFilterClose}
        onSave={handleFilterSave}
        selectedValues={openFilter ? selectedFilters[openFilter] : []}
      />

      {loading ? (
        <div className="events-loading-container">
          <Loader />
          <p>Loading events...</p>
        </div>
      ) : error ? (
        <div className="events-error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      ) : events.length === 0 ? (
        <div className="events-empty-container">
          <EventIcon sx={{ fontSize: 60, color: "#a0aec0" }} />
          <p>No events found. Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="event-page-list">
          {events.map((event) => (
            <div key={event.id}>
              <Link
                to={`/event/${event.id}`}
                className="event-page-card-link"
                onClick={(e) => handleEventClick(event.id, e)}
              >
                <div className="event-page-card">
                  <div className="event-page-logo-container">
                    <img
                      src={
                        event.image ||
                        "https://d3mvy6uwazuxyu.cloudfront.net/event_icon.png"
                      }
                      alt={event.name}
                      className="event-page-logo"
                      onError={(e) => {
                        e.target.src =
                          "https://d3mvy6uwazuxyu.cloudfront.net/event_icon.png"; // Fallback image if event image fails to load
                      }}
                    />
                  </div>
                  <div className="event-page-details">
                    <h2 className="event-page-title">{event.name}</h2>
                    <p className="event-page-date">
                      <span role="img" aria-label="calendar">
                        📅
                      </span>{" "}
                      {event.date}
                    </p>
                    {event.location && (
                      <p className="event-page-location">
                        <span role="img" aria-label="location">
                          📍
                        </span>{" "}
                        {event.location}
                      </p>
                    )}
                    {event.type && (
                      <p className="event-page-type">
                        <span role="img" aria-label="type">
                          🏆
                        </span>{" "}
                        {event.type}
                      </p>
                    )}
                  </div>
                  <div className="event-page-toggle-icon">
                    {expandedEvent === event.id ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )}
                  </div>
                </div>
              </Link>

              {expandedEvent === event.id && (
                <div className="event-page-expanded-content">
                  {loadingEventDetails && !eventDetails[event.id] ? (
                    <div className="event-page-loading">
                      <Loader size={24} />
                      <span>Loading event details...</span>
                    </div>
                  ) : eventErrors[event.id] ? (
                    <div className="event-page-error">
                      <p>{eventErrors[event.id]}</p>
                      <button onClick={() => handleRetryEventDetails(event.id)}>
                        Try Again
                      </button>
                    </div>
                  ) : eventDetails[event.id]?.games?.length > 0 ? (
                    <div className="event-page-games-list">
                      {eventDetails[event.id].games.map((game, index) => (
                        <Link
                          to={`/game/${game.game_id}`}
                          key={index}
                          className="event-page-game-row"
                        >
                          <div className="event-page-game-header">
                            <div className="event-page-game-date">
                              {game.date}
                            </div>
                          </div>
                          <div className="event-page-game-teams">
                            <div className="event-page-team">
                              <div className="event-page-team-info">
                                <span className="event-page-team-name">
                                  {game.team1}
                                </span>
                                <span
                                  className={`event-page-team-score ${
                                    game.team1_score > game.team2_score
                                      ? "winning-score"
                                      : ""
                                  }`}
                                >
                                  {game.team1_score > game.team2_score && (
                                    <KeyboardDoubleArrowRightRoundedIcon />
                                  )}
                                  {game.team1_score || 0}
                                </span>
                              </div>
                              <div className="event-page-score-divider">vs</div>
                              <div className="event-page-team-info">
                                <span className="event-page-team-name">
                                  {game.team2}
                                </span>
                                <span
                                  className={`event-page-team-score ${
                                    game.team2_score > game.team1_score
                                      ? "winning-score"
                                      : ""
                                  }`}
                                >
                                  {game.team2_score > game.team1_score && (
                                    <KeyboardDoubleArrowRightRoundedIcon />
                                  )}
                                  {game.team2_score || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="event-page-game-stats">
                            <div className="event-page-stat">
                              <div className="event-page-stat-label">
                                Points
                              </div>
                              <div>
                                {game.team1_top_players &&
                                  game.team1_top_players[0] && (
                                    <>
                                      {formatPlayerName(
                                        game.team1_top_players[0].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team1_top_players[0].points}
                                      </span>
                                      <br />
                                    </>
                                  )}
                                {game.team2_top_players &&
                                  game.team2_top_players[0] && (
                                    <>
                                      {formatPlayerName(
                                        game.team2_top_players[0].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team2_top_players[0].points}
                                      </span>
                                    </>
                                  )}
                              </div>
                            </div>
                            <div className="event-page-stat">
                              <div className="event-page-stat-label">
                                Rebounds
                              </div>
                              <div>
                                {game.team1_top_players &&
                                  game.team1_top_players[1] && (
                                    <>
                                      {formatPlayerName(
                                        game.team1_top_players[1].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team1_top_players[1].rebounds}
                                      </span>
                                      <br />
                                    </>
                                  )}
                                {game.team2_top_players &&
                                  game.team2_top_players[1] && (
                                    <>
                                      {formatPlayerName(
                                        game.team2_top_players[1].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team2_top_players[1].rebounds}
                                      </span>
                                    </>
                                  )}
                              </div>
                            </div>
                            <div className="event-page-stat">
                              <div className="event-page-stat-label">
                                Assists
                              </div>
                              <div>
                                {game.team1_top_players &&
                                  game.team1_top_players[2] && (
                                    <>
                                      {formatPlayerName(
                                        game.team1_top_players[2].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team1_top_players[2].assists}
                                      </span>
                                      <br />
                                    </>
                                  )}
                                {game.team2_top_players &&
                                  game.team2_top_players[2] && (
                                    <>
                                      {formatPlayerName(
                                        game.team2_top_players[2].name
                                      )}{" "}
                                      <span className="event-page-stat-value">
                                        {game.team2_top_players[2].assists}
                                      </span>
                                    </>
                                  )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="event-page-no-games">
                      <p>No games available for this event.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
};

export default Events;
